// SPDX-FileCopyrightText: Copyright 2025 OP Financial Group (https://op.fi). All Rights Reserved.
//
// SPDX-License-Identifier: LicenseRef-OpAllRightsReserved

import React, { useContext, useState } from 'react';
import { Action, Alert, Col, Loading, Row } from '@op/opds.opux';
import message from '../../messages';
import { agreementTypes, mapPdfErrorMessageContent, getPdf } from '../../components/PdfHandler';
import UseWindowWidth from '../../hooks/useWindowWidth';
import './manualSigning.scss';
import MainHeader from '../../components/MainHeader';
import ManualSigningFooter from './ManualSigningFooter';
import { ContextWrapper } from '../../context/ContextWrapper';

interface PdfState {
    isLoading: boolean;
    error: boolean;
}

const ManualSigning: React.FC = () => {
    const viewThreshold: number = 760;

    const width: number = UseWindowWidth();

    const [pdfState, setPdfState] = useState<PdfState>({
        isLoading: false,
        error: false,
    });

    const { isAdminFlow, isDataSavingInProgress } = useContext(ContextWrapper);

    if (isDataSavingInProgress) {
        return (
            <div className="opux-spacing-top-margin-double opux-align-center">
                <Loading />
            </div>
        );
    }

    return (
        <div id="manualSigning" data-testid="manualSigning">
            <MainHeader />
            <Row middle="xs" className="row-formatting">
                <Col className="number-circle" data-testid="stepOneCircle">
                    1
                </Col>
                <Col xs className="col-formatting" data-testid="stepOneText">
                    {isAdminFlow ? message('downloadYourBusinessTextIsAdminFlow') : message('downloadYourBusinessText')}
                    <Action
                        id="pdfLinkOne"
                        inline
                        onClick={() => {
                            setPdfState({ error: false, isLoading: true });
                            getPdf(agreementTypes.manualSigning, setPdfState, true);
                        }}
                        data-testid="pdfLinkOne"
                    >
                        {isAdminFlow ? message('preFilledPdfTextIsAdminFlow') : message('preFilledPdfText')}
                    </Action>
                    {isAdminFlow ? message('toComputerIsAdminFlow') : message('toComputer')}
                </Col>
            </Row>
            <Row middle="xs" className="row-formatting">
                <Col className="number-circle" data-testid="stepTwoCircle">
                    2
                </Col>
                <Col xs className="col-formatting" data-testid="stepTwoText">
                    {message('signAllSignatoriesText')}
                </Col>
            </Row>
            <Row middle="xs" className="row-formatting">
                <Col className="number-circle" data-testid="stepThreeCircle">
                    3
                </Col>
                <Col xs className="col-formatting" data-testid="stepThreeText">
                    {message('postAgreementToText')}
                    <br />
                    {width > viewThreshold ? (
                        <address id="mailAddress" className="mail-address" data-testid="mailAddress">
                            {message('address1')}
                            <br />
                            {message('address2')}
                            <br />
                            {message('address3')}
                        </address>
                    ) : (
                        <address id="mailAddress" data-testid="mailAddress">
                            {message('addressFull')}
                        </address>
                    )}
                    {message('eMessageText')}
                </Col>
            </Row>
            <Row className="row-formatting" data-testid="supportContact">
                {message('supportContact')}
            </Row>
            {pdfState.isLoading && (
                <div className="opux-spacing-top-margin-double opux-align-center" data-testid="loading">
                    <Loading />
                </div>
            )}
            {pdfState.error && (
                <div className="opux-spacing-top-margin-double" data-testid="errorAlert">
                    <Alert type="critical">
                        {message('noPdfAlertManualSigningText')}

                        {mapPdfErrorMessageContent(pdfState)}
                    </Alert>
                </div>
            )}
            <ManualSigningFooter {...{ setPdfState, width }} />
        </div>
    );
};

export default ManualSigning;
